import { useEffect, useState, type FormEvent } from 'react';
import {
  loadWebProfile,
  saveWebProfileName,
  type WebProfile,
} from '../services/portal';

type ProfilePanelProps = {
  userId: string;
  accountEmail?: string | null;
  onSignOut: () => Promise<void>;
};

export function ProfilePanel({ userId, accountEmail, onSignOut }: ProfilePanelProps) {
  const [profile, setProfile] = useState<WebProfile | null>(null);
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    loadWebProfile(userId)
      .then((value) => {
        if (!active) return;
        setProfile(value);
        setFullName(value?.full_name ?? '');
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'Could not load your profile.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [userId]);

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await saveWebProfileName(userId, fullName);
      setProfile(updated);
      setFullName(updated.full_name ?? '');
      setNotice('Profile updated. The same name will now appear across mobile and web.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your profile.');
    } finally {
      setSaving(false);
    }
  }

  const initials = (profile?.full_name || accountEmail || 'G')
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <section className="portal-content-card profile-card">
      <div className="profile-identity">
        <div className="profile-avatar">{initials}</div>
        <div>
          <p className="eyebrow">Profile</p>
          <h2>{profile?.full_name || 'Add your name'}</h2>
          <p>{profile?.email || accountEmail || 'Gathrd account'}</p>
        </div>
      </div>

      {loading ? (
        <div className="portal-empty">Loading profile…</div>
      ) : (
        <form className="portal-form profile-form" onSubmit={handleSave}>
          <label>
            Display name
            <input value={fullName} onChange={(event) => setFullName(event.target.value)} placeholder="Your name" />
          </label>
          <div className="portal-form-actions profile-actions">
            <button className="primary-button" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save name'}</button>
          </div>
        </form>
      )}

      {notice && <p className="portal-success">{notice}</p>}
      {error && <p className="error-banner portal-inline-error">{error}</p>}

      <div className="profile-link-grid">
        <a href="https://wegathrd.com/download.html" target="_blank" rel="noreferrer"><strong>Get the mobile app</strong><span>Camera, Auto-Gather, QR scanning, and full mobile companion experience.</span></a>
        <a href="https://wegathrd.com/privacy.html" target="_blank" rel="noreferrer"><strong>Privacy Policy</strong><span>How Gathrd handles personal information and event data.</span></a>
        <a href="https://wegathrd.com/terms.html" target="_blank" rel="noreferrer"><strong>Terms of Service</strong><span>Terms governing use of Gathrd.</span></a>
        <a href="https://wegathrd.com/cookies.html" target="_blank" rel="noreferrer"><strong>Cookie Policy</strong><span>Cookie and browser-storage information.</span></a>
        <a href="mailto:support@wegathrd.com?subject=Gathrd%20Support"><strong>Contact support</strong><span>support@wegathrd.com</span></a>
      </div>

      <div className="profile-signout-row">
        <button className="secondary-button" type="button" onClick={() => void onSignOut()}>Sign out</button>
      </div>
    </section>
  );
}
